import React from 'react';
import Command from './_Command';
import { GlobalDialogs } from '../../pdf/GlobalDialogs';
import { dateToLocaleString, readableBytes } from '../../utils';
import { mdiInformationOutline } from '@mdi/js';
import DocPropsDialog from '../Layout/Dialogs/DocPropsDialog';
import { type IDocumentProps, type IPermission, type IFontItem } from '../../pdf/Interfaces';
import { ApryseSDK } from '../../pdf/ApryseSDK';
import i18next from 'i18next';

interface IDocMetadata {
    title: string;
    subject: string;
    producer: string;
    application: string;
    header: string;
}

const getDocumentProps = async (): Promise<IDocumentProps> => {
    const doc = ApryseSDK.documentViewer.getDocument();
    const pdfDoc = await doc.getPDFDoc();

    const pdfDocInfo = await pdfDoc.getDocInfo();
    const metadata = (await doc.getMetadata()) as IDocMetadata;
    const securityHandler = await pdfDoc.getSecurityHandler();
    const docFileSize = await doc.getFileSize(1);

    const documentPermissionsObject = await ApryseSDK.permissionMapping.reduce(
        async (current, [key, enumNumber]) => {
            let languageKey = 'docPropsTable.permissions.';

            if (key === 'SignDigitally') {
                /* 1 = e_no_changes_allowed
                 * 2 = e_formfilling_signing_allowed
                 * 3 = e_annotating_formfilling_signing_allowed
                 * 4 = e_unrestricted
                 */
                const digSigPerm = await pdfDoc.getDigitalSignaturePermissions();
                languageKey += 'SignDigitally.' + digSigPerm;
            } else {
                const permitted = securityHandler
                    ? await securityHandler.getPermission(enumNumber)
                    : true;
                languageKey += permitted ? 'Permitted' : 'Forbidden';
            }

            return {
                ...(await current),
                [key]: i18next.t(languageKey),
            };
        },
        Promise.resolve({} as IPermission),
    );

    const embeddedFonts = await ApryseSDK.getEmbeddedFonts();

    const fontInfos = await Promise.all(
        embeddedFonts.map(async font => {
            return (await ApryseSDK.getFontInfo(font)) as IFontItem;
        }),
    );

    const fontItemComparer = (a: IFontItem, b: IFontItem): -1 | 0 | 1 => {
        if (a.ActualFont === b.ActualFont) {
            return 0;
        }
        return a.ActualFont < b.ActualFont ? -1 : 1;
    };

    const fontsInformationObject = fontInfos.sort(fontItemComparer).reduce((obj, font, index) => {
        obj[`${font.ActualFont}_${index}`] = {
            Type: i18next.t('docPropsTable.fontTypes.' + font.Type),
            Encoding: font.Encoding,
            ActualFont: font.ActualFont,
            ActualFontType: font.ActualFontType,
        };
        return obj;
    }, {} as Record<string, IFontItem>);

    const pdfAConformance = await ApryseSDK.instance.Core.PDFNet.PDFACompliance.getDeclaredConformance(pdfDoc);

    return {
        Description: {
            FileInformation: {
                FileName: decodeURIComponent(doc.getFilename()),
                CreationDate: dateToLocaleString(await pdfDocInfo.getCreationDate()),
                ModDate: dateToLocaleString(await pdfDocInfo.getModDate()),
                FileSize: `${readableBytes(docFileSize.toString())} (${docFileSize.toLocaleString(
                    window.navigator.language,
                )} Bytes)`,
            },
            DocumentInformation: {
                Title: metadata.title,
                Author: await pdfDocInfo.getAuthor(),
                Subject: metadata.subject,
                Keywords: await pdfDocInfo.getKeywords(),
                PDFCreatedWith: metadata.producer,
                Application: metadata.application,
                PDFVersion: Number(metadata.header.replace('%PDF-', '')),
                pdfaid: i18next.t('docPropsTable.pdfAConformance.' + pdfAConformance),
                NumberOfPages: ApryseSDK.pageCount,
            },
        },
        Security: {
            DocumentPermissions: {
                ...documentPermissionsObject,
            },
        },
        Fonts: {
            ...fontsInformationObject,
        },
    };
};

export default class DocPropsController extends Command {
    constructor() {
        super({
            Key: 'showDocProps',
            Enabled: true,
            Path: mdiInformationOutline,
            Callback: async () => {
                try {
                    const documentProps = await getDocumentProps();
                    GlobalDialogs.showDialog(
                        '',
                        <DocPropsDialog
                            onClose={() => GlobalDialogs.closeDialog()}
                            documentProps={documentProps}
                        />,
                    );
                } catch (e) {
                    GlobalDialogs.showError((e as Error)?.message);
                }
            },
        });
    }
}
